import { useState } from 'react';
import { Button, useToast, ColumnDefinition } from 'barte-design-system';
import { TableSkeleton } from '@shared/components';
import { useGetCompanyPlansBySeller } from '@backoffice/services';
import { useSetActiveCompanyPlan } from '@shared/services';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { BACKOFFICE_QUERIES } from '@backoffice/services';

import type { ICompanyPlanBySeller } from '@backoffice/types';

import { ModalSetDefaultPlan } from './components';
import styles from './section-company-plans.module.css';
import type { SectionCompanyPlansProps } from './section-company-plans.type';

export const SectionCompanyPlans = ({ companyId, onSelectCompanyPlan }: SectionCompanyPlansProps) => {
    const { t } = useTranslation();
    const toast = useToast();
    const [page, setPage] = useState(1);
    const [isSetDefaultModalOpen, setIsSetDefaultModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<ICompanyPlanBySeller | null>(null);

    const queryClient = useQueryClient();
    const { data: response, isLoading, refetch } = useGetCompanyPlansBySeller({
        companyId,
        filters: { page, limit: 20 }
    });

    const { mutateAsync: setActivePlan, isPending: isPendingSetActive } = useSetActiveCompanyPlan({
        companyId
    });

    const handleOpenSetDefaultModal = (record: ICompanyPlanBySeller) => {
        setSelectedPlan(record);
        setIsSetDefaultModalOpen(true);
    };

    const handleConfirmSetDefault = async (planType: "EARLY" | "TIMELINE") => {
        if (!selectedPlan) return;

        const response = await setActivePlan({
            planId: selectedPlan.id,
            type: planType,
        });

        if (response.success) {
            toast.success({
                title: t('backoffice.pageModalCompanyPlanFormUpdateSuccess'),
            });
            setIsSetDefaultModalOpen(false);
            queryClient.refetchQueries({ queryKey: [BACKOFFICE_QUERIES.COMPANY_PLANS_BY_SELLER] });
            refetch();
        } else {
            toast.error({
                title: response?.message,
            });
        }
    };

    const plans = response?.data?.data || [];

    const columns: ColumnDefinition<ICompanyPlanBySeller>[] = [
        {
            key: 'id',
            title: t('backoffice.pageCompanyPlansTableColumnId'),
            dataIndex: 'id'
        },
        {
            key: 'name',
            title: t('backoffice.pageCompanyPlansTableColumnName'),
            dataIndex: 'name'
        },
        {
            key: 'earlyPlan',
            title: t('backoffice.pageCompanyPlansTableColumnEarlyPlan'),
            dataIndex: 'id',
            render: (_, record) => {
                const isDefault = record.idPlanCompany === record.id || record.isDefault === 1;
                return isDefault ? (
                    <span className={styles.defaultBadge}>✓ {t('backoffice.pageCompanyPlansDefaultPlanBadge')}</span>
                ) : '-';
            }
        },
        {
            key: 'timelinePlan',
            title: t('backoffice.pageCompanyPlansTableColumnTimelinePlan'),
            dataIndex: 'id',
            render: (_, record) => {
                const isDefaultTimeline = record.idPlanCompanyTimeline === record.id || record.isDefaultTimeline === 1;
                return isDefaultTimeline ? (
                    <span className={styles.defaultBadge}>✓ {t('backoffice.pageCompanyPlansDefaultPlanBadge')}</span>
                ) : '-';
            }
        },
        {
            key: 'sellersCount',
            title: t('backoffice.pageCompanyPlansTableColumnSellersCount'),
            dataIndex: 'idCompanies',
            render: (idCompanies: number[]) => idCompanies?.length || 0
        },
        {
            key: 'actions',
            title: '',
            dataIndex: 'id',
            width: '12.5rem',
            render: (_, record) => {
                return (
                    <Button
                        variant="outline"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSetDefaultModal(record);
                        }}
                    >
                        {t('backoffice.pageCompanyPlansSetDefaultButton')}
                    </Button>
                )
            }
        }
    ];

    return (
        <div className={styles.container}>
            <TableSkeleton<ICompanyPlanBySeller>
                isLoading={isLoading}
                columns={columns}
                data={plans}
                rowKey={(record) => record.id || Math.random().toString()}
                pagination={{
                    currentPage: page,
                    totalPages: response?.data?.pagination?.pages || 1,
                    onPageChange: setPage
                }}
                onRowClick={(record) => onSelectCompanyPlan(record.id)}
            />

            <ModalSetDefaultPlan
                isOpen={isSetDefaultModalOpen}
                onClose={() => setIsSetDefaultModalOpen(false)}
                onConfirm={handleConfirmSetDefault}
                isLoading={isPendingSetActive}
            />
        </div>
    );
};
